import type { Condition, SubCondition } from '@brickloop/core/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api, type LotBricklinkInfo, type SyncResult, type SyncStatus } from '../api';
import { canWrite, isOwner, useAuth } from '../auth';
import { OpenStockCard } from '../components/OpenStockCard';
import { CostForm, ReorderCard, StatusForm, STATUS_TONE, STATUS_WORD } from '../components/StockEntryTools';
import { PartOutCard } from '../components/PartOutCard';
import { ColorSwatch, ItemImage } from '../components/ItemImage';
import { PriceSuggestionsModal } from '../components/PriceSuggestionsModal';
import { LotMarketplaces } from '../components/LotMarketplaces';
import { MarketCard } from '../components/Market';
import type { MarketLotRow } from '../market';
import { useLocations } from '../queries';
import { Badge, Button, Card, Checkbox, ErrorMessage, Field, Input, Notice, PageHeader, Select, Spinner, Stat, Textarea, money, number, unitMoney, unitPlain, cn, useConfirm, when } from '../ui';

interface Entry {
  id: number;
  batchName: string;
  purchaseId: number;
  purchaseDescription: string;
  locationId: number | null;
  locationCode: string | null;
  qtyInitial: number;
  qtyRemaining: number;
  unitCost: string;
  status: 'good' | 'damaged' | 'quarantine' | 'in_transit';
  createdAt: string;
}
interface Movement {
  id: number;
  stockEntryId: number;
  delta: number;
  reason: string;
  note: string | null;
  createdAt: string;
}

const TABS = [
  ['stock', 'Stock'],
  ['marketplaces', 'Marketplaces'],
  ['market', 'BrickLink market'],
  ['ledger', 'Ledger'],
] as const;
const STATUS_BADGE: Record<string, 'green' | 'blue' | 'slate' | 'amber'> = { live: 'green', stockroom: 'blue', draft: 'amber', sold_out: 'slate' };

const RULE_LABEL: Record<string, string> = { breakeven: 'break even (cost)', floor: 'floor', cost_plus: 'cost plus 50%', quick: 'quick sale', market: 'in line with the market', patient: 'patient' };

export function LotDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const writable = canWrite(user);
  const qc = useQueryClient();
  const locations = useLocations();

  const detail = useQuery({
    queryKey: ['lot', id],
    queryFn: () => api<{ lot: MarketLotRow; entries: Entry[]; movements: Movement[] }>(`/api/v1/lots/${id}`),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['lot', id] });
    void qc.invalidateQueries({ queryKey: ['lots'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const lot = detail.data?.lot;
  const [form, setForm] = useState({ price: '', priceLocked: false, remarks: '', description: '', ean: '', bulk: '1', status: 'draft', subCondition: '' });
  useEffect(() => {
    if (!lot) return;
    setForm({
      price: lot.price ? unitPlain(lot.price) : '',
      priceLocked: lot.priceLocked,
      remarks: lot.remarks ?? '',
      description: lot.description ?? '',
      ean: lot.ean ?? '',
      bulk: String(lot.bulk),
      status: lot.status,
      subCondition: lot.subCondition ?? '',
    });
  }, [lot]);

  const save = useMutation({
    mutationFn: () =>
      api(`/api/v1/lots/${id}`, {
        method: 'PATCH',
        body: {
          price: form.price === '' ? null : form.price,
          priceLocked: form.priceLocked,
          remarks: form.remarks || null,
          description: form.description || null,
          ean: form.ean || null,
          bulk: Number(form.bulk) || 1,
          status: form.status,
          subCondition: (form.subCondition || null) as SubCondition | null,
        },
      }),
    onSuccess: refresh,
  });

  const owner = isOwner(user);
  const bl = useQuery({ queryKey: ['lot-bricklink', id], queryFn: () => api<LotBricklinkInfo>(`/api/v1/lots/${id}/bricklink`) });
  const syncStatus = useQuery({ queryKey: ['sync-status'], queryFn: () => api<SyncStatus>('/api/v1/bricklink/sync/status'), enabled: owner });
  const takeDown = useMutation({
    mutationFn: () => api<{ result: SyncResult }>(`/api/v1/lots/${id}/bricklink/take-down`, { method: 'POST', body: {} }).then((r) => r.result),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lot-bricklink', id] });
      void qc.invalidateQueries({ queryKey: ['sync-status'] });
    },
  });
  const { confirm, dialog } = useConfirm();
  const navigate = useNavigate();
  // Fix a lot added as the wrong condition. Says what will happen first; the stock may join an existing lot.
  const changeCondition = useMutation({
    mutationFn: async () => {
      const { preview } = await api<{ preview: { from: string; to: string; qty: number; mergeInto: { id: number; qtyTotal: number } | null } }>(`/api/v1/lots/${id}/condition-change`);
      const word = preview.to === 'N' ? 'new' : 'used';
      const message = preview.mergeInto
        ? `Change this lot to ${word}? Its ${preview.qty} piece${preview.qty === 1 ? '' : 's'} will join the ${word} lot you already have (${preview.mergeInto.qtyTotal} there). Each piece keeps its own cost and bin.`
        : `Change this lot to ${word}? Its ${preview.qty} piece${preview.qty === 1 ? '' : 's'} keep their cost and bin. The price stays as it is, so check it.`;
      if (!(await confirm(message, { label: `Change to ${word}` }))) return null;
      return api<{ result: { lotId: number; merged: boolean } }>(`/api/v1/lots/${id}/condition-change`, { method: 'POST', body: {} }).then((r) => r.result);
    },
    onSuccess: (r) => {
      if (!r) return;
      for (const k of ['lots', 'dashboard', 'lots-attention', 'item-inventory', 'put-away']) void qc.invalidateQueries({ queryKey: [k] });
      if (r.merged) navigate(`/lots/${r.lotId}`);
      else refresh();
    },
  });
  const stopRule = useMutation({
    mutationFn: () => api(`/api/v1/lots/${id}`, { method: 'PATCH', body: { priceRule: null } }),
    onSuccess: refresh,
  });
  const move = useMutation({
    mutationFn: ({ entryId, locationId }: { entryId: number; locationId: number | null }) =>
      api(`/api/v1/stock-entries/${entryId}/move`, { body: { locationId } }),
    onSuccess: refresh,
  });
  const [adjusting, setAdjusting] = useState<Entry | null>(null);
  const [statusFor, setStatusFor] = useState<Entry | null>(null);
  const [costFor, setCostFor] = useState<Entry | null>(null);
  const [params, setParams] = useSearchParams();
  const [suggesting, setSuggesting] = useState(params.get('suggest') === '1');

  if (detail.isPending) return <Spinner />;
  if (detail.error || !lot) return <ErrorMessage error={detail.error} />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  const entries = detail.data.entries;
  const heldQty = entries.reduce((s, e) => s + e.qtyRemaining, 0);
  const avgCost = heldQty > 0 ? entries.reduce((s, e) => s + e.qtyRemaining * Number(e.unitCost), 0) / heldQty : null;
  const tab = TABS.some(([k]) => k === params.get('tab')) ? (params.get('tab') as (typeof TABS)[number][0]) : 'stock';
  const margin = avgCost !== null && lot.price && Number(lot.price) > 0 ? Math.round(((Number(lot.price) - avgCost) / Number(lot.price)) * 100) : null;

  return (
    <>
      {dialog}
      {suggesting && <PriceSuggestionsModal lotId={lot.id} writable={writable} onClose={() => setSuggesting(false)} />}
      <PageHeader
        title={`${lot.itemNo} · ${lot.colorName}`}
        subtitle={
          <>
            <Link to="/items?view=lots" className="hover:underline">
              Inventory
            </Link>{' '}
            /{' '}
            <Link to={`/items/${lot.itemId}`} className="hover:underline">
              {lot.itemName}
            </Link>{' '}
            · {lot.condition === 'N' ? 'New' : 'Used'}
          </>
        }
        actions={
          <>
            <Badge tone={STATUS_BADGE[lot.status] ?? 'slate'}>{lot.status.replace('_', ' ')}</Badge>
            {writable && (
              <Button type="submit" form="lot-form" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            )}
          </>
        }
      />
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Stat label="On hand" value={number(lot.qtyTotal)} note={lot.buildable > 0 ? `${number(lot.buildable)} more can be built` : 'In stock now'} />
          <Stat label="On order" value={number(lot.onOrder)} note={lot.onOrder > 0 ? 'On open purchase orders' : 'Nothing coming'} />
          <Stat label="Your price" value={lot.price ? unitMoney(lot.price) : '—'} note={lot.priceLocked ? 'Locked' : lot.priceRule ? 'Kept up to date overnight' : 'Set by hand'} />
          <Stat label="Average cost" value={avgCost === null ? '—' : unitMoney(avgCost)} note={margin === null ? 'Per piece in stock' : `Margin ${margin}% at your price`} />
          <Stat label="Stock value" value={avgCost === null ? '—' : money((heldQty * avgCost).toFixed(4))} note="At cost" />
        </div>

        <Card title="Primary information">
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
          <div className="flex items-start gap-4">
            <ItemImage itemType={lot.itemType} itemNo={lot.itemNo} colorId={lot.colorId} className="size-24" />
            <dl className="space-y-1 text-sm">
              <div>
                <dt className="sr-only">Name</dt>
                <dd className="font-medium">{lot.itemName}</dd>
              </div>
              <div className="text-slate-600">{lot.categoryName ?? 'No category'}</div>
              <div>
                <ColorSwatch rgb={lot.colorRgb} name={lot.colorName} />
              </div>
              <div className="text-slate-600">
                {lot.condition === 'N' ? 'New' : 'Used'}
                {lot.subCondition ? ` · ${lot.subCondition === 'C' ? 'complete' : lot.subCondition === 'I' ? 'incomplete' : 'sealed'}` : ''}
                {writable && (
                  <button type="button" disabled={changeCondition.isPending} className="ml-2 text-sm text-blue-700 hover:underline" onClick={() => changeCondition.mutate()}>
                    {changeCondition.isPending ? 'Working…' : `Change to ${lot.condition === 'N' ? 'used' : 'new'}`}
                  </button>
                )}
                {changeCondition.error && (
                  <div className="mt-1 text-sm text-red-600">{changeCondition.error instanceof Error ? changeCondition.error.message : 'Could not change it'}</div>
                )}
              </div>
              {lot.buildable > 0 && (
                <div className="text-sm text-slate-600">
                  {number(lot.buildable)} more can be built from loose parts (<Link to={`/items/${lot.itemId}?tab=parts`} className="text-blue-700 hover:underline">parts list</Link>)
                </div>
              )}
              <div>
                <Link to={`/items/${lot.itemId}`} className="text-sm text-blue-700 hover:underline">
                  Open the item record ({lot.itemNo})
                </Link>
              </div>
              <div className="text-xs text-slate-500" title="BrickLink's own ID for this lot in your store. It is set when the lot is sent to BrickLink or matched to its BrickLink lot, and used for later updates.">
                {lot.bricklinkLotId ? `BrickLink lot ID ${lot.bricklinkLotId}` : 'Not on BrickLink yet'}
              </div>
            </dl>
          </div>

            </div>
            <div className="lg:col-span-2">
          <form id="lot-form" onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
            <Field label="Price" hint={lot.priceLocked ? 'Locked: automatic pricing and bulk edits leave it alone.' : undefined}>
              <Input inputMode="decimal" disabled={!writable} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              {writable && (
                <button type="button" className="mt-1 text-sm text-blue-700 hover:underline" onClick={() => setSuggesting(true)}>
                  Suggest a price…
                </button>
              )}
              {lot.priceRule && (
                <div className="mt-1 text-xs text-slate-600">
                  Kept up to date overnight ({RULE_LABEL[lot.priceRule] ?? lot.priceRule}).{' '}
                  {writable && (
                    <button type="button" className="text-blue-700 hover:underline" onClick={() => stopRule.mutate()}>
                      Stop
                    </button>
                  )}
                </div>
              )}
            </Field>
            <Field
              label="Status"
              hint={
                (form.status === 'draft' || form.status === 'sold_out') && lot.bricklinkLotId
                  ? `This lot is on BrickLink (lot ${lot.bricklinkLotId}). ${form.status === 'draft' ? 'Draft' : 'Sold out'} does not remove it there: it stays for sale at its last quantity until you change it on BrickLink.`
                  : 'Live and Stockroom lots are the ones sent to BrickLink. Draft lots are never sent.'
              }
            >
              <Select disabled={!writable} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="draft">Draft</option>
                <option value="live">Live</option>
                <option value="stockroom">Stockroom</option>
                <option value="sold_out">Sold out</option>
              </Select>
            </Field>
            {(lot.itemType === 'SET' || lot.itemType === 'MINIFIG') && (
              <Field label="Completeness" hint={lot.bricklinkLotId ? 'Save here, then use Sync’s completeness XML download or change it on BrickLink. BrickLink cannot update this through its API.' : 'Required before a set can be sent to BrickLink.'}>
                <Select disabled={!writable} value={form.subCondition} onChange={(e) => setForm({ ...form, subCondition: e.target.value })}>
                  <option value="" disabled={!!lot.bricklinkLotId}>Not specified</option>
                  <option value="C">Complete</option>
                  <option value="I">Incomplete</option>
                  <option value="S">Sealed</option>
                </Select>
              </Field>
            )}
            <Field label="EAN">
              <Input disabled={!writable} value={form.ean} onChange={(e) => setForm({ ...form, ean: e.target.value })} />
            </Field>
            <Field label="Bulk" hint="Pieces sold in multiples of this.">
              <Input inputMode="numeric" disabled={!writable} value={form.bulk} onChange={(e) => setForm({ ...form, bulk: e.target.value })} />
            </Field>
            <Field label="Remarks (private)" className="sm:col-span-2">
              <Input disabled={!writable} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
            </Field>
            <Field label="Description (public)" className="sm:col-span-2">
              <Textarea rows={2} disabled={!writable} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
            <div className="flex items-center justify-between sm:col-span-2">
              <Checkbox label="Lock price" checked={form.priceLocked} onChange={(v) => writable && setForm({ ...form, priceLocked: v })} />
            </div>
            <div className="sm:col-span-2">
              <ErrorMessage error={save.error} />
              {save.isSuccess && <Notice tone="green">Saved.</Notice>}
            </div>
          </form>

            </div>
          </div>
        </Card>

        <div role="tablist" className="flex flex-wrap gap-1 border-b border-slate-200">
          {TABS.map(([k, label]) => (
            <button
              key={k}
              role="tab"
              type="button"
              aria-selected={tab === k}
              onClick={() => {
                const next = new URLSearchParams(params);
                next.set('tab', k);
                setParams(next, { replace: true });
              }}
              className={cn('-mb-px border-b-2 px-4 py-2 text-sm font-medium', tab === k ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800')}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'stock' && (
          <div className="space-y-4">
        <Card title="Stock entries" className="lg:col-span-3">
          <div className="overflow-x-auto">
            <table className="data w-full text-sm">
              <thead>
                <tr>
                  <th>From</th>
                  <th className="text-right">Bought</th>
                  <th className="text-right">Left</th>
                  <th className="text-right">Unit cost</th>
                  <th>Status</th>
                  <th>Location</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {detail.data.entries.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <Link to={`/purchases/${e.purchaseId}`} className="hover:underline">
                        {e.purchaseDescription || `Purchase ${e.purchaseId}`}
                      </Link>
                      <div className="text-xs text-slate-500">
                        {e.batchName} · {when(e.createdAt)}
                      </div>
                    </td>
                    <td className="text-right tabular-nums">{number(e.qtyInitial)}</td>
                    <td className="text-right tabular-nums">{number(e.qtyRemaining)}</td>
                    <td className="text-right tabular-nums">{Number(e.unitCost) > 0 ? unitMoney(e.unitCost) : <span className="text-slate-500">free</span>}</td>
                    <td>
                      <Badge tone={STATUS_TONE[e.status]}>{STATUS_WORD[e.status]}</Badge>
                    </td>
                    <td>
                      {writable ? (
                        <Select
                          aria-label={`Location for entry ${e.id}`}
                          className="w-36"
                          value={e.locationId ?? ''}
                          onChange={(ev) => move.mutate({ entryId: e.id, locationId: ev.target.value === '' ? null : Number(ev.target.value) })}
                        >
                          <option value="">Unplaced</option>
                          {locations.data?.map((l) => (
                            <option key={l.id} value={l.id}>
                              {l.code}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        (e.locationCode ?? 'Unplaced')
                      )}
                    </td>
                    <td className="space-x-1 text-right whitespace-nowrap">
                      {writable && e.qtyRemaining > 0 && (
                        <>
                          <Button variant="secondary" small onClick={() => { setStatusFor(e); setCostFor(null); setAdjusting(null); }} disabled={e.status === 'in_transit'}>
                            Status
                          </Button>
                          <Button variant="secondary" small onClick={() => { setCostFor(e); setStatusFor(null); setAdjusting(null); }}>
                            Cost
                          </Button>
                          <Button variant="secondary" small onClick={() => { setAdjusting(e); setStatusFor(null); setCostFor(null); }}>
                            Adjust
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2">
            <ErrorMessage error={move.error} />
          </div>
          {statusFor && <StatusForm entry={statusFor} onClose={() => setStatusFor(null)} onDone={() => { setStatusFor(null); refresh(); }} />}
          {costFor && <CostForm entry={costFor} onClose={() => setCostFor(null)} onDone={() => { setCostFor(null); refresh(); }} />}
          {adjusting && <AdjustForm entry={adjusting} onClose={() => setAdjusting(null)} onDone={() => { setAdjusting(null); refresh(); }} />}
        </Card>
        {writable && lot.qtyTotal > 0 && <OpenStockCard lotId={lot.id} itemNo={lot.itemNo} condition={lot.condition as Condition} onHand={lot.qtyTotal} />}
        {writable && lot.itemType === 'SET' && lot.qtyTotal > 0 && <PartOutCard lotId={lot.id} setNo={lot.itemNo} onHand={lot.qtyTotal} />}

        {writable && <ReorderCard itemId={lot.itemId} colorId={lot.colorId} condition={lot.condition} />}
          </div>
        )}
        {tab === 'marketplaces' && (
          <div className="space-y-4">
        <LotMarketplaces lotId={lot.id} itemId={lot.itemId} canRefresh={writable} />
        {bl.data && (bl.data.listing || bl.data.bricklinkLotId) && (
          <Card title="On BrickLink" className="lg:col-span-3">
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
              <div className="space-y-0.5">
                {bl.data.listing ? (
                  <div>
                    BrickLink lot <strong>{bl.data.listing.remoteId}</strong>: <strong>{number(bl.data.listing.qty)}</strong> {bl.data.listing.stockroom ? 'in your stockroom' : 'for sale'}
                    {bl.data.listing.price ? ` at ${unitMoney(bl.data.listing.price)}` : ''}.
                  </div>
                ) : (
                  <div>BrickLink had no lot for this when it was last read.</div>
                )}
                <div className="text-xs text-slate-500">
                  Last read {when(bl.data.readAt)}. {bl.data.ambiguous ? 'BrickLink holds this item, colour and condition more than once, so none is picked.' : ''}
                  {bl.data.canTakeDown && ' This lot is not for sale here (Draft, sold out or empty) but is still for sale on BrickLink.'}
                </div>
              </div>
              {owner && bl.data.canTakeDown && (
                <Button
                  variant="secondary"
                  disabled={takeDown.isPending || syncStatus.data?.writesOn === false}
                  title={syncStatus.data?.writesOn === false ? 'Switch writing on from the Sync page first' : undefined}
                  onClick={async () => {
                    if (await confirm(`Take ${lot.itemNo} ${lot.colorName} down from your BrickLink store? It is set to quantity 0 and kept there (not deleted).`, { label: 'Take it down', danger: true })) takeDown.mutate();
                  }}
                >
                  {takeDown.isPending ? 'Taking it down…' : 'Take down from BrickLink'}
                </Button>
              )}
            </div>
            <ErrorMessage error={takeDown.error} />
            {takeDown.data && (
              <div className="mt-2">
                <Notice tone={takeDown.data.succeeded === 1 ? 'green' : 'amber'}>
                  {takeDown.data.succeeded === 1 ? 'Taken down: BrickLink now shows quantity 0.' : takeDown.data.stoppedBecause ?? 'Nothing was changed.'}
                </Notice>
              </div>
            )}
          </Card>
        )}
          </div>
        )}
        {tab === 'market' && (
          <div className="space-y-4">
        <MarketCard lotId={lot.id} market={lot.market} writable={writable} />
          </div>
        )}
        {tab === 'ledger' && (
          <div className="space-y-4">
        <Card title="Ledger" className="lg:col-span-3">
          {detail.data.movements.length === 0 ? (
            <p className="text-sm text-slate-500">No movements yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100 text-sm">
              {detail.data.movements.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-3 py-1.5">
                  <span className="flex items-center gap-2">
                    <Badge tone={m.delta > 0 ? 'green' : 'amber'}>
                      {m.delta > 0 ? '+' : ''}
                      {m.delta}
                    </Badge>
                    <span className="capitalize">{m.reason.replace('_', ' ')}</span>
                    {m.note && <span className="text-slate-500">· {m.note}</span>}
                  </span>
                  <span className="text-xs text-slate-500">{when(m.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
          </div>
        )}
      </div>
    </>
  );
}

function AdjustForm({ entry, onClose, onDone }: { entry: Entry; onClose: () => void; onDone: () => void }) {
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('adjust');
  const [note, setNote] = useState('');
  const adjust = useMutation({
    mutationFn: () => api(`/api/v1/stock-entries/${entry.id}/adjust`, { body: { delta: Number(delta), reason, note: note || null } }),
    onSuccess: onDone,
  });
  return (
    <form
      className="mt-4 flex flex-wrap items-end gap-3 rounded-md bg-slate-50 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        adjust.mutate();
      }}
    >
      <Field label={`Change quantity (${entry.qtyRemaining} left)`} hint="Use a minus to remove: -3">
        <Input inputMode="numeric" autoFocus value={delta} onChange={(e) => setDelta(e.target.value)} />
      </Field>
      <Field label="Reason">
        <Select value={reason} onChange={(e) => setReason(e.target.value)}>
          <option value="adjust">Correction</option>
          <option value="found">Found</option>
          <option value="lost">Lost</option>
          <option value="damaged">Damaged</option>
        </Select>
      </Field>
      <Field label="Note">
        <Input value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <Button type="submit" disabled={!Number.isInteger(Number(delta)) || Number(delta) === 0 || adjust.isPending}>
        Apply
      </Button>
      <Button variant="ghost" onClick={onClose}>
        Cancel
      </Button>
      <div className="basis-full">
        <ErrorMessage error={adjust.error} />
      </div>
    </form>
  );
}
